import _, { isNil } from 'lodash';
import { DateTime } from 'luxon';
import OvertimePayPeriodType from 'types/enum/OvertimePayPeriodType';
import OvertimeRuleType from 'types/enum/OvertimeRuleType';
import IMemberOvertimePeriod from 'types/MemberOvertimePeriod';
import IOrganizationOvertimePeriod from 'types/OrganizationOvertimePeriod';
import { organizationOvertimeUtils } from 'utils';
import { dateTimeFromUtcISO } from './dateUtils';
import { t } from './localize';
import { getChangeDateString } from './wageUtils';

export function getCurrentOvertime(
  overtimes: IMemberOvertimePeriod[],
  orgOvertime: IOrganizationOvertimePeriod | undefined
) {
  let currentMemberOvertimePeriod = _.maxBy(overtimes, (overtime) =>
    _.isNil(overtime?.deletedOn) &&
    getEffectiveDate(overtime, orgOvertime).startOf('day') <= DateTime.local().startOf('day')
      ? DateTime.fromISO(overtime.changeDate).toSeconds()
      : 0
  );
  // if we don't have a member overtime period then create a dummy one that is following the org overtime period
  if (isNil(currentMemberOvertimePeriod)) {
    currentMemberOvertimePeriod = {
      adhereToOrg: true,
      changeDate: '1970-01-05T00:00:00',
      createdOn: '2020-10-02T00:00:00',
      id: 'e3810d6e-4ed1-4997-92f3-3aec45080280',
      weeklyOvertime: null,
      dailyOvertime: null,
      dailyDoubletime: null,
      seventhDayOvertime: null,
      seventhDayDoubletime: null,
      saturdayMultiplier: null,
      sundayMultiplier: null,
      submittedOn: '',
      updatedOn: '1970-01-05T00:00:00',
      deletedOn: null,
      memberId: '',
      cursor: '',
    };
  }
  return currentMemberOvertimePeriod;
}

export function getFutureOvertime(
  overtimes: IMemberOvertimePeriod[],
  orgOvertime: IOrganizationOvertimePeriod | undefined
) {
  return _.first(
    overtimes.filter((overtime) => {
      return (
        _.isNil(overtime?.deletedOn) &&
        getEffectiveDate(overtime, orgOvertime).startOf('day') > DateTime.local().startOf('day')
      );
    })
  );
}

export function getEffectiveDate(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): DateTime {
  let date = dateTimeFromUtcISO(overtime.changeDate);
  if (orgOvertime && orgOvertime.startTime) {
    const day = date.weekday;
    const orgDay = dateTimeFromUtcISO(orgOvertime.startTime).weekday;
    let dayOffset = orgDay - day;
    if (dayOffset < 0) {
      dayOffset = 7 + dayOffset;
    }
    date = date.plus({ days: dayOffset });
  }
  return date;
}

export function getEffectiveDateString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  return getChangeDateString(getEffectiveDate(overtime, orgOvertime).toISO()) ?? '';
}

export function getOvertimePreference(overtime: IMemberOvertimePeriod): string {
  if (overtime.adhereToOrg) {
    return t('Follow Company Settings');
  } else if (
    overtime.weeklyOvertime === 40 &&
    overtime.dailyOvertime === 8 &&
    overtime.dailyDoubletime === 12 &&
    overtime.seventhDayOvertime &&
    overtime.seventhDayDoubletime === 8 &&
    overtime.saturdayMultiplier === null &&
    overtime.sundayMultiplier === null
  ) {
    return t('California Settings');
  } else if (
    overtime.weeklyOvertime === 40 &&
    overtime.dailyOvertime === null &&
    overtime.dailyDoubletime === null &&
    !overtime.seventhDayOvertime &&
    overtime.seventhDayDoubletime === null &&
    overtime.saturdayMultiplier === null &&
    overtime.sundayMultiplier === null
  ) {
    return t('Standard Settings');
  }
  return t('Custom Settings');
}

export function getOvertimePayPeriodType(overtime: IMemberOvertimePeriod): OvertimePayPeriodType {
  if (overtime.adhereToOrg) {
    return OvertimePayPeriodType.FOLLOW_COMPANY;
  } else if (
    overtime.weeklyOvertime === 40 &&
    overtime.dailyOvertime === 8 &&
    overtime.dailyDoubletime === 12 &&
    overtime.seventhDayOvertime &&
    overtime.seventhDayDoubletime === 8 &&
    overtime.saturdayMultiplier === null &&
    overtime.sundayMultiplier === null
  ) {
    return OvertimePayPeriodType.CALIFORNIA;
  } else if (
    overtime.weeklyOvertime === 40 &&
    overtime.dailyOvertime === null &&
    overtime.dailyDoubletime === null &&
    !overtime.seventhDayOvertime &&
    overtime.seventhDayDoubletime === null &&
    overtime.saturdayMultiplier === null &&
    overtime.sundayMultiplier === null
  ) {
    return OvertimePayPeriodType.STANDARD;
  }
  return OvertimePayPeriodType.CUSTOM;
}

export function getRuleTitle(type: OvertimeRuleType): string {
  switch (type) {
    case OvertimeRuleType.WEEKLY_OVERTIME:
      return t('Weekly Overtime (wage x 1.5)');
    case OvertimeRuleType.DAILY_OVERTIME:
      return t('Daily Overtime (wage x 1.5)');
    case OvertimeRuleType.DAILY_DOUBLE_OVERTIME:
      return t('Daily Overtime (wage x 2.0)');
    case OvertimeRuleType.SEVENTH_DAY_OVERTIME:
      return t('7th Day Overtime (wage x 1.5)');
    case OvertimeRuleType.SEVENTH_DAY_DOUBLE_OVERTIME:
      return t('7th Day Overtime (wage x 2.0)');
    case OvertimeRuleType.SATURDAY_MULTIPLE:
      return t('Saturday Pay Rate');
    case OvertimeRuleType.SUNDAY_MULTIPLE:
      return t('Sunday Pay Rate');
  }
}

export function getRuleValue(
  type: OvertimeRuleType,
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  switch (type) {
    case OvertimeRuleType.WEEKLY_OVERTIME:
      return getWeeklyOvertimeString(overtime, orgOvertime);
    case OvertimeRuleType.DAILY_OVERTIME:
      return getDailyOvertimeString(overtime, orgOvertime);
    case OvertimeRuleType.DAILY_DOUBLE_OVERTIME:
      return getDailyDoubleOString(overtime, orgOvertime);
    case OvertimeRuleType.SEVENTH_DAY_OVERTIME:
      return getSeventhOvertimeDayString(overtime, orgOvertime);
    case OvertimeRuleType.SEVENTH_DAY_DOUBLE_OVERTIME:
      return getSeventhDayDoubleString(overtime, orgOvertime);
    case OvertimeRuleType.SATURDAY_MULTIPLE:
      return getSaturdayMultipleString(overtime, orgOvertime);
    case OvertimeRuleType.SUNDAY_MULTIPLE:
      return getSundayMultipleString(overtime, orgOvertime);
  }
}

export function getWeeklyOvertimeString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getWeeklyOvertimeString(orgOvertime);
    }
    return '---';
  }
  if (!isNil(overtime.weeklyOvertime)) {
    return t('after') + ' ' + overtime.weeklyOvertime + ' ' + t('hours');
  }
  return '---';
}

export function getDailyOvertimeString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getDailyOvertimeString(orgOvertime);
    }
    return '---';
  }
  if (!isNil(overtime.dailyOvertime)) {
    return t('after') + ' ' + overtime.dailyOvertime + ' ' + t('hours');
  }
  return '---';
}

export function getDailyDoubleOString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getDailyDoubleOString(orgOvertime);
    }
    return '---';
  }
  if (!isNil(overtime.dailyDoubletime)) {
    return t('after') + ' ' + overtime.dailyDoubletime + ' ' + t('hours');
  }
  return '---';
}

export function getSeventhOvertimeDayString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getSeventhOvertimeDayString(orgOvertime);
    }
    return '---';
  }
  if (!_.isNil(overtime.seventhDayOvertime)) {
    return overtime.seventhDayOvertime ? t('Yes') : '---';
  }
  return '---';
}

export function getSeventhDayDoubleString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getSeventhDayDoubleString(orgOvertime);
    }
    return '---';
  }
  if (!isNil(overtime.seventhDayDoubletime)) {
    return t('after') + ' ' + overtime.seventhDayDoubletime + ' ' + t('hours');
  }
  return '---';
}

export function getSaturdayMultipleString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getSaturdayMultipleString(orgOvertime);
    }
    return '---';
  }
  if (overtime.saturdayMultiplier) {
    return t('wage x') + ' ' + overtime.saturdayMultiplier;
  }
  return '---';
}

export function getSundayMultipleString(
  overtime: IMemberOvertimePeriod,
  orgOvertime: IOrganizationOvertimePeriod | undefined
): string {
  if (overtime.adhereToOrg) {
    if (orgOvertime) {
      return organizationOvertimeUtils.getSundayMultipleString(orgOvertime);
    }
    return '---';
  }
  if (overtime.sundayMultiplier) {
    return t('wage x') + ' ' + overtime.sundayMultiplier;
  }
  return '---';
}
